import { namespace } from '@common-config/constants';
import { useAppSelector } from '@common-reducers/hooks/store.hook';
import { labelEmptyStateCopy } from '@common-reducers/labels-reducer/label-view-stats';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { waContactsCacheSelectors } from '@common-reducers/DBServiceThunks';
import { waOpenChatTool } from '@extension-context-content/thunks/WhatsappThunks';

// #770: WhatsApp lists only chats that have a conversation and are not archived. When the active
// label has members but none of them qualify, explain why the list is empty instead of leaving
// it silently blank under a non-zero badge.
export default function LabelEmptyState({ dir }: { dir: 'ltr' | 'rtl' }) {
    const { t } = useTranslation(namespace);
    const [expanded, setExpanded] = useState(false);
    const contactNames = useAppSelector((state) => waContactsCacheSelectors.selectEntities(state));
    const view = useAppSelector((state) => state.LabelReducer.labelViewStats);
    const activeLabelId = useAppSelector((state) => state.LabelReducer.activeLabelId);
    if (!view || view.labelId !== activeLabelId) return null;
    const copy = labelEmptyStateCopy(view.stats);
    if (!copy) return null;

    return (
        <div
            data-bt-feature="labelFilterRow"
            role="status"
            style={{
                direction: dir,
                position: 'absolute',
                top: '100%',
                insetInlineStart: 16,
                insetInlineEnd: 16,
                zIndex: 98,
                marginTop: 8,
                padding: '12px 16px',
                borderRadius: 8,
                border: '1px solid #dbdcdf',
                backgroundColor: 'var(--color-surface)',
                fontSize: 13,
                lineHeight: 1.4,
                whiteSpace: 'normal',
            }}
        >
            <div style={{ fontWeight: 600 }}>{t(copy.titleKey, { count: copy.titleCount })}</div>
            {copy.titleKey === 'labels.emptyView.noConversation' && copy.archivedCount > 0 && (
                <div>{t('labels.emptyView.archived', { count: copy.archivedCount })}</div>
            )}
            <div style={{ opacity: 0.7 }}>{t('labels.emptyView.hint')}</div>
            {view.noConversationIds.length > 0 && (
                <button
                    type="button"
                    onClick={() => setExpanded(!expanded)}
                    style={{ marginTop: 6, color: 'var(--color-accent)', fontWeight: 600, cursor: 'pointer' }}
                >
                    {expanded ? t('labels.emptyView.hideContacts') : t('labels.emptyView.showContacts')}
                </button>
            )}
            {expanded && (
                <ul style={{ marginTop: 6, maxHeight: 200, overflowY: 'auto' }}>
                    {view.noConversationIds.map((chatId) => {
                        const cached = contactNames[chatId];
                        const label = cached?.name || cached?.displayName || chatId.split('@')[0];
                        return (
                            <li key={chatId}>
                                <button
                                    type="button"
                                    onClick={() => { void waOpenChatTool({ chatId }); }}
                                    style={{ cursor: 'pointer', textAlign: 'start', width: '100%', padding: '4px 0' }}
                                >
                                    {label}
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
